import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { useLocations } from '../../../hooks/locations'
import { useCurrentUser } from '../../../hooks/users'
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridSortModel,
  GridToolbarContainer,
  useGridApiContext,
} from '@mui/x-data-grid'
import { Button, Card, CardContent, Checkbox, ClickAwayListener, Paper, Popper, Stack, Typography, useTheme } from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import {
  CanonicalPeriod,
  getPeriodYear,
  MetricState,
  metricStateLabels,
  PERIODS,
  stateColorMapping,
} from '../common'
import { LoadingValue } from './PortfolioLoading'
import PortfolioSectionTitle from './PortfolioSectionTitle'

type SortDirection = 'asc' | 'desc'

type PortfolioPropertyRow = {
  id: number
  name: string
  country: string | null
  insurance: {
    revenue?: number | string | null
    structure?: number | string | null
    contents?: number | string | null
  } | null
}

type MetricStatusSpec = {
  key: string
  label?: string
  width?: number
  hiddenByDefault?: boolean
  loading?: boolean
  getValue: (propertyId: number) => MetricState | null
}

type MetricBase = {
  key: string
  label: string
  perPeriod?: boolean
  width?: number
  loading?: boolean
  getHeaderName?: (period: CanonicalPeriod) => string
  status?: MetricStatusSpec
}

export type ScoreMetricSpec = MetricBase & {
  kind: 'score'
  getValue: (propertyId: number, period?: CanonicalPeriod) => number | null
  renderScore: (value: number | null) => ReactNode
}

export type CurrencyMetricSpec = MetricBase & {
  kind: 'currency'
  getValue: (propertyId: number, period?: CanonicalPeriod) => number | null
  format?: 'compact' | 'fixed0' | 'fixed1'
  suffix?: string
}

export type NumberMetricSpec = MetricBase & {
  kind: 'number'
  getValue: (propertyId: number, period?: CanonicalPeriod) => number | null
  format?: 'compact' | 'fixed0' | 'fixed1'
  suffix?: string
}

export type TextMetricSpec = MetricBase & {
  kind: 'text'
  getValue: (propertyId: number, period?: CanonicalPeriod) => string | null
}

export type CustomMetricSpec<TValue = unknown> = MetricBase & {
  kind: 'custom'
  getValue: (propertyId: number, period?: CanonicalPeriod) => TValue | null
  renderCell: (value: TValue | null, ctx: { propertyId: number; period?: CanonicalPeriod; currencySymbol: string }) => ReactNode
}

export type PortfolioMetricSpec = ScoreMetricSpec | CurrencyMetricSpec | NumberMetricSpec | TextMetricSpec | CustomMetricSpec

export type PortfolioPropertyBreakdownTableProps = {
  metrics: PortfolioMetricSpec[]
  selectedPeriod?: CanonicalPeriod
  title?: string
  height?: number
  filterPropertyIds?: number[]
  onViewProperty?: (propertyId: number) => void
  initialSort?: { metricKey: string; period?: CanonicalPeriod; sort?: SortDirection }
}

function parseNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function formatNumber(n: number, format: 'compact' | 'fixed0' | 'fixed1' = 'compact'): string {
  if (format === 'compact') {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n)
  }
  const maximumFractionDigits = format === 'fixed1' ? 1 : 0
  return new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(n)
}

function StatusChip({ value }: { value?: MetricState | null }) {
  if (!value) return <span>—</span>
  const label = metricStateLabels[value] || value
  const color = stateColorMapping[value]
  return (
    <span style={{ background: color, color: 'white', fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>
      {label}
    </span>
  )
}

type PropertyInfoColumn = { field: string; label: string }

const PROPERTY_INFO_COLUMNS: PropertyInfoColumn[] = [
  { field: 'fin_revenue', label: 'Revenue' },
  { field: 'fin_structure', label: 'Structure Value' },
  { field: 'fin_contents', label: 'Contents Value' },
]

type PropertyBreakdownToolbarProps = {
  title?: string
  propertyInfoOpen?: boolean
  onOpenPropertyInfo?: (anchorEl: HTMLElement) => void
  onClosePropertyInfo?: () => void
  onOpenFilterPanel?: (anchorEl: HTMLElement) => void
}

function PropertyBreakdownToolbar({
  title = 'Portfolio Breakdown',
  propertyInfoOpen = false,
  onOpenPropertyInfo,
  onClosePropertyInfo,
  onOpenFilterPanel,
}: PropertyBreakdownToolbarProps) {
  const apiRef = useGridApiContext()
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', px: 0, py: 1 }}>
      <PortfolioSectionTitle title={title} />
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
        <Button
          variant="outlined"
          size="small"
          startIcon={<ViewColumnIcon />}
          onClick={e => {
            if (propertyInfoOpen) {
              onClosePropertyInfo?.()
              return
            }
            apiRef.current?.hidePreferences?.()
            onOpenPropertyInfo?.(e.currentTarget)
          }}>
          Financials
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltIcon />}
          onMouseDown={e => onOpenFilterPanel?.(e.currentTarget)}
          onClick={() => {
            onClosePropertyInfo?.()
            const preferencePanel = (apiRef.current?.state as any)?.preferencePanel
            if (preferencePanel?.open && preferencePanel?.openedPanelValue === 'filters') {
              apiRef.current?.hideFilterPanel?.()
              return
            }
            apiRef.current?.showFilterPanel?.()
          }}>
          Filter
        </Button>
      </Stack>
    </GridToolbarContainer>
  )
}

function deriveSortField(field: string, selectedPeriod?: CanonicalPeriod): string {
  if (!selectedPeriod) return field
  const match = field.match(/^(.*)_(short|mid|long)$/)
  if (!match) return field
  const base = match[1]
  return `${base}_${selectedPeriod}`
}

export default function PortfolioPropertyBreakdownTable({
  metrics,
  selectedPeriod,
  title = 'Portfolio Breakdown',
  height = 520,
  filterPropertyIds,
  onViewProperty,
  initialSort,
}: PortfolioPropertyBreakdownTableProps) {
  const theme = useTheme()
  const { groupId } = useSelector((state: RootState) => state.system)
  const locationsEnabled = groupId !== undefined && groupId !== null
  const { data: locations, isLoading: locationsLoading } = useLocations({ group_id: groupId }, locationsEnabled)
  const { currencySymbol } = useCurrentUser()

  const rows: PortfolioPropertyRow[] = useMemo(() => {
    const allRows = (locations || []).map((p: any) => ({
      id: p.id,
      name: p.name || `Property ${p.id}`,
      country: p.address?.country ?? null,
      insurance: p.insurance ?? null,
    }))
    if (!filterPropertyIds) return allRows
    const allowed = new Set(filterPropertyIds)
    return allRows.filter(r => allowed.has(r.id))
  }, [filterPropertyIds, locations])

  const periodsToShow = useMemo(() => (selectedPeriod ? [selectedPeriod] : PERIODS), [selectedPeriod])

  const defaultVisibilityModel = useMemo((): GridColumnVisibilityModel => {
    const model: GridColumnVisibilityModel = {}
    PROPERTY_INFO_COLUMNS.forEach(c => {
      model[c.field] = false
    })
    metrics.forEach(metric => {
      if (!metric.status) return
      if (metric.status.hiddenByDefault ?? true) {
        model[metric.status.key] = false
      }
    })
    return model
  }, [metrics])

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => defaultVisibilityModel)

  useEffect(() => {
    setColumnVisibilityModel(prev => ({ ...defaultVisibilityModel, ...prev }))
  }, [defaultVisibilityModel])

  const defaultSortModel = useMemo((): GridSortModel => {
    if (initialSort) {
      const period = initialSort.period ?? selectedPeriod ?? CanonicalPeriod.LONG_TERM
      return [{ field: `${initialSort.metricKey}_${period}`, sort: initialSort.sort ?? 'desc' }]
    }
    const firstMetric = metrics[0]
    if (!firstMetric) return []
    if (firstMetric.perPeriod) {
      const period = selectedPeriod ?? CanonicalPeriod.LONG_TERM
      return [{ field: `${firstMetric.key}_${period}`, sort: 'desc' }]
    }
    return [{ field: firstMetric.key, sort: 'desc' }]
  }, [initialSort, metrics, selectedPeriod])

  const [sortModel, setSortModel] = useState<GridSortModel>(() => defaultSortModel)

  useEffect(() => {
    if (!selectedPeriod) return
    setSortModel(prev => {
      if (prev.length === 0) return prev
      return prev.map(item => ({ ...item, field: deriveSortField(item.field, selectedPeriod) }))
    })
  }, [selectedPeriod])

  const renderMetricValue = useCallback(
    (metric: PortfolioMetricSpec, value: unknown, ctx: { propertyId: number; period?: CanonicalPeriod }): ReactNode => {
      if (metric.kind === 'score') {
        const n = typeof value === 'number' ? value : typeof value === 'string' ? parseFloat(value) : NaN
        const v = Number.isFinite(n) ? (n as number) : null
        return metric.renderScore(v)
      }
      if (metric.kind === 'currency') {
        const n = parseNullableNumber(value)
        if (typeof n !== 'number') return null
        const txt = `${currencySymbol}${formatNumber(n, metric.format ?? 'compact')}${metric.suffix ? ` ${metric.suffix}` : ''}`
        return txt
      }
      if (metric.kind === 'number') {
        const n = parseNullableNumber(value)
        if (typeof n !== 'number') return null
        const txt = `${formatNumber(n, metric.format ?? 'compact')}${metric.suffix ? ` ${metric.suffix}` : ''}`
        return txt
      }
      if (metric.kind === 'text') {
        return typeof value === 'string' ? value : value != null ? String(value) : null
      }
      return metric.renderCell(value as any, { propertyId: ctx.propertyId, period: ctx.period, currencySymbol })
    },
    [currencySymbol],
  )

  const columns: GridColDef<PortfolioPropertyRow>[] = useMemo(() => {
    const cols: GridColDef<PortfolioPropertyRow>[] = [
      { field: 'name', headerName: 'Property', width: 220 },
      { field: 'country', headerName: 'Country', width: 120 },
      {
        field: 'fin_revenue',
        headerName: 'Revenue',
        width: 150,
        valueGetter: p => parseNullableNumber(p.row.insurance?.revenue),
        renderCell: p => (
          <LoadingValue loading={locationsLoading} width={110} fallback="—">
            {typeof p.value === 'number' ? `${currencySymbol}${formatNumber(p.value, 'compact')}` : null}
          </LoadingValue>
        ),
      },
      {
        field: 'fin_structure',
        headerName: 'Structure Value',
        width: 170,
        valueGetter: p => parseNullableNumber(p.row.insurance?.structure),
        renderCell: p => (
          <LoadingValue loading={locationsLoading} width={110} fallback="—">
            {typeof p.value === 'number' ? `${currencySymbol}${formatNumber(p.value, 'compact')}` : null}
          </LoadingValue>
        ),
      },
      {
        field: 'fin_contents',
        headerName: 'Contents Value',
        width: 170,
        valueGetter: p => parseNullableNumber(p.row.insurance?.contents),
        renderCell: p => (
          <LoadingValue loading={locationsLoading} width={110} fallback="—">
            {typeof p.value === 'number' ? `${currencySymbol}${formatNumber(p.value, 'compact')}` : null}
          </LoadingValue>
        ),
      },
    ]

    metrics.forEach(metric => {
      if (metric.perPeriod) {
        periodsToShow.forEach(period => {
          const field = `${metric.key}_${period}`
          const headerName = metric.getHeaderName ? metric.getHeaderName(period) : `${metric.label} (${getPeriodYear(period)})`
          cols.push({
            field,
            headerName,
            width: metric.width ?? 150,
            valueGetter: p => metric.getValue(p.row.id, period),
            renderCell: p => {
              const content = renderMetricValue(metric, p.value, { propertyId: p.row.id, period })
              const loadingProps =
                metric.kind === 'score'
                  ? { variant: 'rounded' as const, width: 72, height: 20 }
                  : { width: 100, height: 18 }
              return (
                <LoadingValue loading={metric.loading} fallback="—" {...loadingProps}>
                  {content}
                </LoadingValue>
              )
            },
          })
        })
      } else {
        cols.push({
          field: metric.key,
          headerName: metric.label,
          width: metric.width ?? 150,
          valueGetter: p => metric.getValue(p.row.id),
          renderCell: p => {
            const content = renderMetricValue(metric, p.value, { propertyId: p.row.id })
            const loadingProps =
              metric.kind === 'score' ? { variant: 'rounded' as const, width: 72, height: 20 } : { width: 100, height: 18 }
            return (
              <LoadingValue loading={metric.loading} fallback="—" {...loadingProps}>
                {content}
              </LoadingValue>
            )
          },
        })
      }

      if (metric.status) {
        cols.push({
          field: metric.status.key,
          headerName: metric.status.label ?? `Status (${metric.label})`,
          width: metric.status.width ?? 140,
          sortable: false,
          valueGetter: p => metric.status?.getValue(p.row.id) ?? null,
          renderCell: p => (
            <LoadingValue loading={metric.status?.loading ?? metric.loading} variant="rounded" width={80} height={18}>
              <StatusChip value={p.value as MetricState | null} />
            </LoadingValue>
          ),
        })
      }
    })

    if (onViewProperty) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <Button size="small" onClick={() => onViewProperty(params.row.id)}>
            View property
          </Button>
        ),
      })
    }

    return cols
  }, [currencySymbol, locationsLoading, metrics, onViewProperty, periodsToShow, renderMetricValue])

  const gridLoading = locationsLoading && rows.length === 0

  const [preferencesAnchorEl, setPreferencesAnchorEl] = useState<HTMLElement | null>(null)
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null)
  const openInfoMenu = useCallback((anchorEl: HTMLElement) => setInfoAnchorEl(anchorEl), [])
  const closeInfoMenu = useCallback(() => setInfoAnchorEl(null), [])

  const isColumnVisible = useCallback(
    (field: string) => columnVisibilityModel[field] !== false,
    [columnVisibilityModel],
  )

  const setColumnVisible = useCallback((field: string, visible: boolean) => {
    setColumnVisibilityModel(prev => ({ ...prev, [field]: visible }))
  }, [])

  return (
    <Card sx={{ overflow: 'hidden' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <div style={{ height, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={r => r.id}
            loading={gridLoading}
            density="compact"
            disableSelectionOnClick
            onRowDoubleClick={params => onViewProperty?.(params.row.id)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            components={{ Toolbar: PropertyBreakdownToolbar }}
            componentsProps={{
              toolbar: {
                title,
                propertyInfoOpen: Boolean(infoAnchorEl),
                onOpenPropertyInfo: openInfoMenu,
                onClosePropertyInfo: closeInfoMenu,
                onOpenFilterPanel: setPreferencesAnchorEl,
              },
              panel: {
                ...(preferencesAnchorEl ? { anchorEl: preferencesAnchorEl } : {}),
                placement: 'bottom-end',
              },
            }}
            onPreferencePanelClose={() => setPreferencesAnchorEl(null)}
            sx={{
              border: 0,
              '& .MuiDataGrid-row': { cursor: onViewProperty ? 'pointer' : 'default' },
              '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.action.hover },
            }}
          />
        </div>

        <Popper open={Boolean(infoAnchorEl)} anchorEl={infoAnchorEl} placement="bottom-end" disablePortal>
          <ClickAwayListener onClickAway={closeInfoMenu} mouseEvent="onMouseUp">
            <Paper elevation={8} sx={{ mt: 1, minWidth: 240, borderRadius: 1 }}>
              <Stack sx={{ px: 1.5, py: 1 }} spacing={0.5}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1, pb: 0.5 }}>
                  Financial columns
                </Typography>
                {PROPERTY_INFO_COLUMNS.map(col => {
                  const checked = isColumnVisible(col.field)
                  return (
                    <Stack
                      key={col.field}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ px: 1, py: 0.25, cursor: 'pointer' }}
                      onClick={() => setColumnVisible(col.field, !checked)}>
                      <Checkbox
                        size="small"
                        checked={checked}
                        onClick={e => e.stopPropagation()}
                        onChange={(_, v) => setColumnVisible(col.field, v)}
                        sx={{ p: 0.5 }}
                      />
                      <Typography variant="body2">{col.label}</Typography>
                    </Stack>
                  )
                })}
              </Stack>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </CardContent>
    </Card>
  )
}
